import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';
import { Communication } from 'src/providers/communication/communication';
import { Me } from 'src/providers/me/me';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

@Component({
  selector: 'smart-number-onboarding',
  templateUrl: './smart-number-onboarding.component.html',
  styleUrls: ['./smart-number-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SmartNumberOnboardingComponent implements OnInit {
  private client = inject(Client);
  private communication = inject(Communication);
  private me = inject(Me);

  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  windowService = inject(WindowService);

  private metadataKey: string;
  private readonly stepId = 'smart-num';

  proNumber = '';
  guestNumber = '';
  isLoading = true;
  fromGettingStarted = false;
  cameFromSubscribePage = false;

  completeStep = async () => {
    try {
      await this.client.addMetadata({
        key: this.metadataKey,
        value: 'completed'
      });

      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      const errorMessage = (err as any)?.error?.message || 'Failed to complete step. Please try again.';
      this.util.showError(errorMessage);
    }
  };

  copyToClipboard(number: string): void {
    this.util.copyToClipBoard(number);
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    const addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.cameFromSubscribePage = params?.cameFromSubscribePage === true;
    this.metadataKey = `onboarding_step.${addressId}.${this.stepId}`;
    this.rightSidePanelService.setDialogPageTitle('Smart Number');
    await this.loadOrCreateNumbers();
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private async loadOrCreateNumbers(): Promise<void> {
    try {
      const existingNumbers = await this.communication.getProxyNumbers();

      const existingPro = existingNumbers?.find(n => n.number_type === 'homekeeper');
      const existingGuest = existingNumbers?.find(n => n.number_type === 'guest');

      if (existingPro && existingGuest) {
        this.proNumber = this.formatPhoneNumber(existingPro.phone_number);
        this.guestNumber = this.formatPhoneNumber(existingGuest.phone_number);
        return;
      }

      const subscriptionTypes = await this.me.getSubscriptionTypes();
      const hasNonTrialPaidSubscription = await this.me.checkIfHasNonTrialPaidSubscription(subscriptionTypes);
      if (!hasNonTrialPaidSubscription) {
        this.util.showError('You must be on a paid subscription to create smart numbers.');
        this.rightSidePanelService.navigateTo('subscription', {});
        return;
      }

      const promises: Promise<any>[] = [];
      if (!existingPro) {
        promises.push(
          this.communication.createProxyNumber('homekeeper').then(response => {
            this.proNumber = this.formatPhoneNumber(response.phone_number);
            localStorage.setItem('cachedProNumber', this.proNumber);
          })
        );
      } else {
        this.proNumber = this.formatPhoneNumber(existingPro.phone_number);
      }

      if (!existingGuest) {
        promises.push(
          this.communication.createProxyNumber('guest').then(response => {
            this.guestNumber = this.formatPhoneNumber(response.phone_number);
            localStorage.setItem('cachedGuestNumber', this.guestNumber);
          })
        );
      } else {
        this.guestNumber = this.formatPhoneNumber(existingGuest.phone_number);
      }

      await Promise.all(promises);
    } catch (err) {
      const errorMessage = (err as any)?.error?.message || 'Failed to load smart numbers. Please try again.';
      this.util.showError(errorMessage);
    }
  }

  private formatPhoneNumber(number: string): string {
    if (!number) {return '';}
    return number.replace(/^\+?\d{1,3}(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
  }
}
